import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCircleQuestion,
  faCopy,
  faDownload,
  faMagnifyingGlass,
  faPencil,
  faUpload,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import App from './App.vue';
import { useAuthStore } from './features/auth/authStore';
import { useConfigStore } from './features/config/configStore';
import { useSetupPreferenceStore } from './features/setup/setupPreferenceStore';
import { configureAnalytics } from './providers/analytics';
import { configureFirebase } from './providers/firebase';
import { initSentry } from './providers/sentry';
import { createRouter } from './router';
import './style.css';

// GA4 Measurement ID (same as in firebase.ts and index.html)
const GA_MEASUREMENT_ID = 'G-3YXYKJK38T';

// Add Font Awesome icons to library
library.add(faArrowUpRightFromSquare);
library.add(faCheck);
library.add(faCircleQuestion);
library.add(faCopy);
library.add(faDownload);
library.add(faMagnifyingGlass);
library.add(faPencil);
library.add(faUpload);
library.add(faUser);
library.add(faXmark);

// Initialize Firebase
configureFirebase();

// Initialize Analytics
configureAnalytics({ measurementId: GA_MEASUREMENT_ID });

// Create app
const app = createApp(App);
const pinia = createPinia();

// Pinia must be installed before any store is used in bootstrap.
app.use(pinia);
app.component('font-awesome-icon', FontAwesomeIcon);

// Bootstrap resolves runtime config and auth state BEFORE installing the router
// and mounting. Installing the router triggers the initial navigation (and its
// auth guard), so config and auth must be settled first — otherwise the guard
// runs against unresolved auth and skips its protected-route redirects.
const bootstrap = async () => {
  const configStore = useConfigStore();
  await configStore.fetchConfig();

  // Initialize Sentry (must happen before app.mount)
  initSentry({
    app,
    dsn: configStore.sentryDsn,
    deploymentId: configStore.deploymentId,
  });

  // Settle auth state before the router's initial navigation guard runs.
  const authStore = useAuthStore();
  await authStore.initialize();

  // Install the router after auth+config are settled, then wait for the initial
  // navigation to resolve before mounting.
  const router = createRouter();
  app.use(router);
  await router.isReady();

  app.mount('#app');

  // Initialize setup preference state from localStorage
  const setupPreferenceStore = useSetupPreferenceStore();
  setupPreferenceStore.initialize();
};

bootstrap().catch((error) => {
  console.error('Failed to bootstrap app:', error);
});
